import React, { useEffect, useMemo, useState } from "react";
import { Button } from "./ui/button";
import {
    Plus,
    Trash,
    Play,
    Warning,
    PiggyBank,
    Lightning,
    DotsSixVertical,
    X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getTopPredictions, simulateTransferPlan } from "../lib/api";

const HIT_VERDICT_STYLE = {
    WORTH_IT: { color: "#9EE8B4", label: "WORTH IT" },
    NOT_WORTH: { color: "#FF2A2A", label: "NOT WORTH" },
    NO_HIT: { color: "#888888", label: "" },
};

function upcomingGws(squad) {
    const startGw = Number(squad?.gameweek || 1);
    return Array.from({ length: Number(squad?.window || 5) }, (_, i) => startGw + i);
}

// ---- Drag payload helpers (native HTML5 DnD, no deps) ----------------------

const DRAG_MIME = "application/x-fa-transfer";

function encodeDrag(kind, id) {
    return JSON.stringify({ kind, id: Number(id) });
}

function parseDrag(evt) {
    try {
        const raw = evt.dataTransfer.getData(DRAG_MIME) || evt.dataTransfer.getData("text/plain");
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

// ---- Chip components ------------------------------------------------------

function DraggableSquadChip({ player, onStart, onPick }) {
    return (
        <div
            draggable
            onDragStart={(e) => {
                const payload = encodeDrag("out", player.fpl_id);
                e.dataTransfer.setData(DRAG_MIME, payload);
                // Kind-specific MIME so the dragover handler can detect the
                // payload type without calling getData() (which is blocked
                // outside of `drop` events).
                e.dataTransfer.setData("application/x-fa-out", payload);
                e.dataTransfer.setData("text/plain", payload);
                e.dataTransfer.effectAllowed = "copy";
                onStart?.();
            }}
            data-testid={`drag-squad-${player.fpl_id}`}
            className="flex items-center gap-2 border border-white/10 bg-black/40 hover:border-fa-cream/60 hover:bg-fa-violet/20 px-2 py-1.5 cursor-grab active:cursor-grabbing transition-colors group"
        >
            <DotsSixVertical size={12} className="text-white/30 group-hover:text-fa-cream" />
            <div className="flex-1 min-w-0">
                <div className="font-mono text-[11px] text-white truncate">{player.web_name}</div>
                <div className="font-mono text-[9px] text-white/50">
                    {player.team_short} · {player.position} · £{player.price?.toFixed?.(1)}m
                </div>
            </div>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPick?.(player.fpl_id); }}
                data-testid={`pick-squad-${player.fpl_id}`}
                title="Pick into first empty OUT slot"
                className="flex-shrink-0 w-6 h-6 border border-white/15 hover:border-red-500/60 hover:bg-red-500/10 hover:text-red-400 text-white/50 transition-colors flex items-center justify-center"
            >
                <Plus size={11} weight="bold" />
            </button>
        </div>
    );
}

function DraggablePoolChip({ player, onPick }) {
    return (
        <div
            draggable
            onDragStart={(e) => {
                const payload = encodeDrag("in", player.id);
                e.dataTransfer.setData(DRAG_MIME, payload);
                e.dataTransfer.setData("application/x-fa-in", payload);
                e.dataTransfer.setData("text/plain", payload);
                e.dataTransfer.effectAllowed = "copy";
            }}
            data-testid={`drag-pool-${player.id}`}
            className="flex items-center gap-2 border border-white/10 bg-black/40 hover:border-fa-mint/60 hover:bg-fa-mint/10 px-2 py-1.5 cursor-grab active:cursor-grabbing transition-colors group"
        >
            <DotsSixVertical size={12} className="text-white/30 group-hover:text-fa-mint" />
            <div className="flex-1 min-w-0">
                <div className="font-mono text-[11px] text-white truncate">{player.name}</div>
                <div className="font-mono text-[9px] text-white/50">
                    {player.team} · £{player.price?.toFixed?.(1)}m · {player.predicted_points_total?.toFixed?.(1)}pts
                </div>
            </div>
            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onPick?.(player.id); }}
                data-testid={`pick-pool-${player.id}`}
                title="Pick into first empty IN slot"
                className="flex-shrink-0 w-6 h-6 border border-white/15 hover:border-fa-mint/60 hover:bg-fa-mint/10 hover:text-fa-mint text-white/50 transition-colors flex items-center justify-center"
            >
                <Plus size={11} weight="bold" />
            </button>
        </div>
    );
}

function DropSlot({ kind, current, onDrop, onClear, testid, placeholder }) {
    const [isOver, setIsOver] = useState(false);
    const accentBorder = kind === "out" ? "border-red-500/60" : "border-fa-mint/60";
    const accentBg = kind === "out" ? "bg-red-500/10" : "bg-fa-mint/10";
    return (
        <div
            onDragOver={(e) => {
                // HTML5 DnD security quirk: getData() is BLOCKED during dragover.
                // We can only inspect dataTransfer.types — encode the slot kind
                // into a custom MIME prefix so we know what kind of drag this is
                // before the drop fires.
                const types = Array.from(e.dataTransfer.types || []);
                const kindPrefix = `application/x-fa-${kind}`;
                if (types.includes(DRAG_MIME) || types.includes(kindPrefix)) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "copy";
                    setIsOver(true);
                }
            }}
            onDragLeave={() => setIsOver(false)}
            onDrop={(e) => {
                e.preventDefault();
                setIsOver(false);
                const payload = parseDrag(e);
                if (payload && payload.kind === kind) {
                    onDrop(payload.id);
                }
            }}
            data-testid={testid}
            data-slot-kind={kind}
            className={`flex items-center gap-2 border border-dashed px-3 py-2 min-h-[44px] transition-colors ${
                isOver
                    ? `${accentBorder} ${accentBg}`
                    : current
                        ? "border-white/20 bg-black/30"
                        : "border-white/10 bg-black/20 text-white/30"
            }`}
        >
            {current ? (
                <>
                    <div className="flex-1 min-w-0">
                        <div className="font-mono text-xs text-white truncate">{current.label}</div>
                        <div className="font-mono text-[9px] text-white/50">{current.sub}</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClear}
                        data-testid={`${testid}-clear`}
                        className="p-1 text-white/40 hover:text-red-500"
                    >
                        <X size={12} weight="bold" />
                    </button>
                </>
            ) : (
                <span className="font-mono text-[10px] uppercase tracking-widest">{placeholder}</span>
            )}
        </div>
    );
}

// ---- Main component -------------------------------------------------------

export default function TransferTimeline({
    managerId,
    squad,
    transfers,
    setTransfers,
    onSimChange,
}) {
    const [predictions, setPredictions] = useState([]);
    const [sim, setSim] = useState(null);
    const [simLoading, setSimLoading] = useState(false);
    const [predsLoading, setPredsLoading] = useState(false);

    useEffect(() => {
        if (!squad) return;
        let cancelled = false;
        setPredsLoading(true);
        getTopPredictions(squad.window || 5, 200)
            .then((data) => {
                if (!cancelled) setPredictions(data.predictions || []);
            })
            .catch(() => toast.error("Failed to load prediction pool"))
            .finally(() => { if (!cancelled) setPredsLoading(false); });
        return () => { cancelled = true; };
    }, [squad]);

    const squadIndex = useMemo(
        () => Object.fromEntries((squad?.squad || []).map((p) => [p.fpl_id, p])),
        [squad],
    );
    const predIndex = useMemo(
        () => Object.fromEntries(predictions.map((p) => [p.id, p])),
        [predictions],
    );

    const addRow = () => {
        const first = upcomingGws(squad)[0] || 1;
        // Stable per-row id avoids array-index-as-key reconciliation glitches
        // when middle rows are removed (drop slots, hit checkbox, GW select all
        // keep their own state correctly).
        const uid = `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
        setTransfers((prev) => [...prev, { uid, gw: first, out_id: "", in_id: "", hit: false }]);
        setSim(null);
    };

    const removeRow = (idx) => {
        setTransfers((prev) => prev.filter((_, i) => i !== idx));
        setSim(null);
    };

    const updateRow = (idx, patch) => {
        setTransfers((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
        setSim(null);
    };

    // Click-to-pick fallback for mobile/accessibility (no drag required).
    // Fills the first row whose matching slot is empty, or creates a new row
    // if every existing slot of that kind is already occupied.
    const pickInto = (kind, id) => {
        const field = kind === "out" ? "out_id" : "in_id";
        setSim(null);
        setTransfers((prev) => {
            const emptyIdx = prev.findIndex((t) => !t[field]);
            if (emptyIdx >= 0) {
                return prev.map((t, i) => (i === emptyIdx ? { ...t, [field]: Number(id) } : t));
            }
            const firstGw = upcomingGws(squad)[0] || 1;
            const uid = `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
            const newRow = { uid, gw: firstGw, out_id: "", in_id: "", hit: false, [field]: Number(id) };
            return [...prev, newRow];
        });
        const label = kind === "out" ? "OUT slot filled" : "IN slot filled";
        toast.success(label, { duration: 1500 });
    };

    const runSim = async () => {
        if (!managerId) return;
        const valid = transfers.filter((t) => t.out_id && t.in_id && t.gw);
        if (valid.length === 0) {
            toast.error("Add at least one valid transfer (drag Out + In)");
            return;
        }
        setSimLoading(true);
        try {
            const body = {
                transfers: valid.map((t) => ({
                    gw: Number(t.gw),
                    out_id: Number(t.out_id),
                    in_id: Number(t.in_id),
                    hit: Boolean(t.hit),
                })),
                window: squad?.window || 5,
                bank: squad?.bank || 0,
            };
            const res = await simulateTransferPlan(managerId, body);
            setSim(res.simulation);
            onSimChange?.(res.simulation);
            toast.success(`Net delta: ${res.simulation.net_delta >= 0 ? "+" : ""}${res.simulation.net_delta} pts`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Simulation failed");
        } finally {
            setSimLoading(false);
        }
    };

    if (!squad) return null;
    const gws = upcomingGws(squad);

    return (
        <div className="border border-white/10 bg-black/40 p-4" data-testid="transfer-timeline">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <Lightning size={16} weight="fill" className="text-fa-cream" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                        Transfer Timeline ({transfers.length}) · drag & drop or tap +
                    </span>
                </div>
                <div className="flex gap-2">
                    <Button
                        type="button"
                        onClick={addRow}
                        data-testid="add-transfer-row"
                        variant="outline"
                        className="bg-transparent border-white/20 hover:bg-white/5 rounded-none font-mono text-xs uppercase h-8"
                    >
                        <Plus size={12} weight="bold" className="mr-1" />
                        Add GW slot
                    </Button>
                    <Button
                        type="button"
                        onClick={runSim}
                        disabled={simLoading || transfers.length === 0 || predsLoading}
                        data-testid="simulate-plan-btn"
                        className="bg-fa-cream hover:bg-fa-creamHover text-black rounded-none font-mono text-xs uppercase h-8"
                    >
                        <Play size={12} weight="fill" className="mr-1" />
                        {simLoading ? "Simulating…" : "Simulate plan"}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_220px] gap-3">
                {/* Left rail: Squad pool */}
                <div className="border border-white/10 bg-black/20 p-2" data-testid="squad-rail">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-red-500" /> My Squad
                    </div>
                    <div className="space-y-1 max-h-[520px] overflow-auto pr-1">
                        {(squad.squad || []).map((p) => (
                            <DraggableSquadChip
                                key={p.fpl_id}
                                player={p}
                                onPick={(id) => pickInto("out", id)}
                            />
                        ))}
                    </div>
                </div>

                {/* Middle: timeline rows */}
                <div className="space-y-2" data-testid="timeline-rows">
                    {transfers.length === 0 ? (
                        <div className="font-mono text-[11px] text-white/40 py-10 text-center border border-dashed border-white/10">
                            Click <span className="text-fa-cream">Add GW slot</span>, then drag a player
                            from <span className="text-red-400">My Squad</span> into the OUT slot and a
                            player from <span className="text-fa-mint">Top Targets</span> into the IN slot.
                            <span className="block mt-1 text-white/30">
                                Or tap <span className="text-fa-cream">+</span> on any chip to auto-fill the first empty slot.
                            </span>
                        </div>
                    ) : (
                        transfers.map((t, idx) => {
                            const outPlayer = squadIndex[t.out_id];
                            const inPlayer = predIndex[t.in_id];
                            const rowKey = t.uid || `legacy-${idx}`;
                            return (
                                <div
                                    key={rowKey}
                                    className="grid grid-cols-[60px_1fr_1fr_80px_30px] gap-2 items-center border border-white/10 bg-black/30 p-2"
                                    data-testid={`transfer-row-${idx}`}
                                >
                                    <select
                                        value={t.gw}
                                        onChange={(e) => updateRow(idx, { gw: Number(e.target.value) })}
                                        data-testid={`transfer-gw-${idx}`}
                                        className="bg-black/40 border border-white/10 text-fa-cream font-mono text-xs h-9 px-2 w-full"
                                    >
                                        {gws.map((g) => (
                                            <option key={g} value={g}>GW {g}</option>
                                        ))}
                                    </select>
                                    <DropSlot
                                        kind="out"
                                        testid={`transfer-out-${idx}`}
                                        placeholder="◀ drop squad player (OUT)"
                                        current={outPlayer && {
                                            label: outPlayer.web_name,
                                            sub: `${outPlayer.team_short} · £${outPlayer.price?.toFixed?.(1)}m`,
                                        }}
                                        onDrop={(id) => updateRow(idx, { out_id: id })}
                                        onClear={() => updateRow(idx, { out_id: "" })}
                                    />
                                    <DropSlot
                                        kind="in"
                                        testid={`transfer-in-${idx}`}
                                        placeholder="drop target (IN) ▶"
                                        current={inPlayer && {
                                            label: inPlayer.name,
                                            sub: `${inPlayer.team} · £${inPlayer.price?.toFixed?.(1)}m · ${inPlayer.predicted_points_total?.toFixed?.(1)}pts`,
                                        }}
                                        onDrop={(id) => updateRow(idx, { in_id: id })}
                                        onClear={() => updateRow(idx, { in_id: "" })}
                                    />
                                    <label className="flex items-center justify-center gap-1 font-mono text-[10px] uppercase text-white/60 cursor-pointer border border-white/10 h-9">
                                        <input
                                            type="checkbox"
                                            checked={t.hit}
                                            onChange={(e) => updateRow(idx, { hit: e.target.checked })}
                                            data-testid={`transfer-hit-${idx}`}
                                            className="w-3 h-3 accent-fa-cream"
                                        />
                                        -4 hit
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => removeRow(idx)}
                                        data-testid={`transfer-remove-${idx}`}
                                        className="p-1 text-white/40 hover:text-red-500 flex items-center justify-center"
                                    >
                                        <Trash size={14} weight="bold" />
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>

                {/* Right rail: Target pool */}
                <div className="border border-white/10 bg-black/20 p-2" data-testid="pool-rail">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-fa-mint" /> Top Targets
                    </div>
                    <div className="space-y-1 max-h-[520px] overflow-auto pr-1">
                        {predsLoading ? (
                            <div className="font-mono text-[10px] text-white/40 py-4 text-center">Loading pool…</div>
                        ) : (
                            predictions.slice(0, 30).map((p) => (
                                <DraggablePoolChip
                                    key={p.id}
                                    player={p}
                                    onPick={(id) => pickInto("in", id)}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>

            {sim && <TransferSimSummary sim={sim} />}
        </div>
    );
}

function TransferSimSummary({ sim }) {
    return (
        <div className="mt-4 border border-fa-cream/40 bg-fa-cream/5 p-3" data-testid="transfer-sim-summary">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 font-mono text-xs">
                <Stat label="Start proj" value={sim.start_projected} />
                <Stat label="Planned proj" value={sim.planned_projected} />
                <Stat
                    label="Hits"
                    value={`-${sim.hits_cost}`}
                    color={sim.hits_cost > 0 ? "#FF8800" : "#888"}
                />
                <Stat
                    label="Net proj"
                    value={sim.net_projected}
                    color={sim.net_delta >= 0 ? "#9EE8B4" : "#FF2A2A"}
                    testid="sim-net-proj"
                />
                <Stat
                    label="Δ Net"
                    value={sim.net_delta >= 0 ? `+${sim.net_delta}` : sim.net_delta}
                    color={sim.net_delta >= 0 ? "#9EE8B4" : "#FF2A2A"}
                    testid="sim-net-delta"
                />
            </div>
            <div className="flex items-center gap-2 mt-3 font-mono text-xs">
                <PiggyBank size={14} weight="bold" className={sim.bank_after < 0 ? "text-red-500" : "text-fa-cream"} />
                <span className="text-white/60">Bank after:</span>
                <span
                    style={{ color: sim.bank_after < 0 ? "#FF2A2A" : "#9EE8B4" }}
                    data-testid="sim-bank-after"
                >
                    £{Number(sim.bank_after).toFixed(1)}m
                </span>
            </div>

            {sim.warnings?.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="sim-warnings">
                    {sim.warnings.map((w) => (
                        <div key={w} className="flex items-start gap-2 font-mono text-[11px] text-red-400">
                            <Warning size={12} weight="bold" className="mt-0.5" />
                            <span>{w}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className="mt-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-2">
                    Per-transfer breakdown
                </div>
                <div className="space-y-1">
                    {sim.transfers.map((t, i) => (
                        <div
                            key={`${t.gw}-${t.out?.id ?? t.out?.name}-${t.in?.id ?? t.in?.name}`}
                            className="flex items-center gap-2 font-mono text-[11px] text-white/70"
                            data-testid={`sim-transfer-${i}`}
                        >
                            <span className="text-fa-cream w-12">GW+{t.gw}</span>
                            <span className="text-red-400 w-20 truncate" title={t.out.name}>{t.out.name}</span>
                            <span className="text-white/40">→</span>
                            <span className="text-green-400 w-28 truncate" title={t.in.name}>{t.in.name}</span>
                            <span
                                className="w-16"
                                style={{ color: t.points_delta >= 0 ? "#9EE8B4" : "#FF2A2A" }}
                            >
                                {t.points_delta >= 0 ? `+${t.points_delta}` : t.points_delta}pts
                            </span>
                            <span
                                className="w-16"
                                style={{ color: t.price_delta <= 0 ? "#9EE8B4" : "#FF8800" }}
                            >
                                {t.price_delta >= 0 ? `+£${t.price_delta}` : `-£${Math.abs(t.price_delta)}`}m
                            </span>
                            {t.hit && (
                                <span
                                    className="ml-auto px-2 py-0.5 text-[10px] uppercase tracking-widest"
                                    style={{ color: HIT_VERDICT_STYLE[t.hit_verdict]?.color || "#888" }}
                                    data-testid={`sim-verdict-${i}`}
                                >
                                    {HIT_VERDICT_STYLE[t.hit_verdict]?.label || "—"}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, color, testid }) {
    return (
        <div className="border border-white/5 px-2 py-1" data-testid={testid}>
            <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
            <div className="text-base" style={color ? { color } : undefined}>{value}</div>
        </div>
    );
}
